import 'isomorphic-fetch';
import React from 'react'
import ReactDOM from 'react-dom';
import axios from 'axios';
import {flashMessage} from 'redux-flash';
import {Provider} from 'react-redux';
import {createStore, applyMiddleware} from 'redux';
import thunkMiddleware from 'redux-thunk'
import {Router, Route, Redirect, hashHistory} from 'react-router';
import {syncHistoryWithStore, routerMiddleware} from 'react-router-redux';
import {middleware as flashMiddleware} from 'redux-flash';
import {persistStore, persistReducer} from 'redux-persist';
import {createHashHistory, useBasename} from 'history';
import {loadTranslations, syncTranslationWithStore, setLocale} from 'react-redux-i18n';
import {isAdmin, isLogged, getAuthorizationSetting} from './helpers/Helpers';
import {setLanguages} from './store/actions/language';
import {getGlobals} from './store/actions/global';
import {PersistGate} from 'redux-persist/es/integration/react';
import storage from 'redux-persist/lib/storage';
import reducers from './store/reducers';
import translations from './config/translations';
import Config from './config';
import asyncComponent from './AsyncComponent';

if (!global._babelPolyfill) {
    require('babel-polyfill');
}

/**
 * Async components: Backend Layout
 */
const MainLayout = asyncComponent(() => import('./components/backend/Layouts/MainLayout'));
const LoginLayout = asyncComponent(() => import('./components/backend/Layouts/LoginLayout'));

/**
 * Async components: Backend
 */
const HomeBackend = asyncComponent(() => import('./pages/backend/Home'));
const WorkspaceBackend = asyncComponent(() => import('./pages/backend/Workspace'));
const AddWorkspaceBackend = asyncComponent(() => import('./pages/backend/AddWorkspace'));
const MediaBackend = asyncComponent(() => import('./pages/backend/Media'));
const UsersBackend = asyncComponent(() => import('./pages/backend/Users'));
const MenuBackend = asyncComponent(() => import('./pages/backend/Menu'));
const PageBackend = asyncComponent(() => import('./pages/backend/Page'));
const BlockBackend = asyncComponent(() => import('./pages/backend/Block'));
const AddPageBackend = asyncComponent(() => import('./pages/backend/AddPage'));
const AddBlockBackend = asyncComponent(() => import('./pages/backend/AddBlock'));
const AddMenuBackend = asyncComponent(() => import('./pages/backend/AddMenu'));
const AddGlobalFieldBackend = asyncComponent(() => import('./pages/backend/AddGlobalField'));
const GlobalFieldBackend = asyncComponent(() => import('./pages/backend/GlobalField'));
const LogoutBackend = asyncComponent(() => import('./pages/backend/LogoutBackend'));


var client = document.getElementById('application').getAttribute('data-client');
let initialLoad = true;
let renderer = document.getElementById('application').getAttribute('data-renderer');
let initialRoute = document.getElementById('application').getAttribute('data-initial-route');
let isAuthorizationOn = document.getElementById('application').getAttribute('data-authorization-on');
if (!client) {
    client = "job-architecture";
}

if (!isAuthorizationOn) {
    isAuthorizationOn = getAuthorizationSetting(renderer, client);
}


/**
 * Middleware
 */
const middleware = [
    thunkMiddleware,
    flashMiddleware(),
    routerMiddleware(hashHistory)
];

/**
 * Persist
 */
const persist = persistReducer({
    key: 'root',
    storage: storage,
    blacklist: [
        'flash',
        'routing',
        'media',
        'i18n'
    ]
}, reducers);

/**
 * Store configuration
 */
const store = createStore(persist, applyMiddleware(...middleware));

/**
 * Async components: Frontend Layout
 */
const FrontLayout = asyncComponent(() => import('./components/' + client + '/Layouts/FrontLayout'));
/**
 * Async components: Frontend
 */

const Page = asyncComponent(() => import('./pages/' + client + '/Page'));
const Preview = asyncComponent(() => import('./pages/' + client + '/Preview'));

var Home = null;
var OngoingActions = null;
var DevTests = null;
var ElementGuidlines = null;
var OngoingActionsArchive = null;

/**
 * Language sync
 */
syncTranslationWithStore(store);

if (renderer !== "backend" && (isAuthorizationOn && isLogged())) {
    store.dispatch(loadTranslations(translations));
    store.dispatch(setLocale(Config.language));
    store.dispatch(setLanguages());
}

const gate = persistStore(store);
const basename = window.location.pathname + window.location.search;

const browserHistory = useBasename(createHashHistory)({
    basename: basename === '/' ? '' : ''
});

const history = syncHistoryWithStore(browserHistory, store);

/**
 * Configure axios
 */
axios.interceptors.response.use((response) => {
    return response;
}, function (error) {
    if (Config.getToken()) {
        let message = 'Something went wrong. Please try again!';
        store.dispatch(
            flashMessage(message, {
                timeout: 0,
                props: {
                    type: 'error'
                }
            })
        );
    }


    return Promise.reject(error);
});

/**
 * Log out version
 */
console.log('version: 6.165');

/**
 * With backend layout
 */
const withBackendLayout = (WrappedComponent) => {
    return class WithAuthorization extends React.Component {
        componentDidMount() {
            document.body.classList.remove('front-page');
            document.body.classList.add('admin-page');
        }

        componentWillMount() {
            const {location: {pathname}} = this.props;

            if (initialLoad && initialRoute) {
                if (initialRoute !== pathname && pathname === '/') {
                    window.location.replace(window.location.href + initialRoute);
                    initialLoad = false;
                }
            }
        }

        render() {
            if ((isAuthorizationOn && isLogged()) || !isAuthorizationOn) {
                return (
                    <MainLayout {...this.props}>
                        <WrappedComponent {...this.props} />
                    </MainLayout>
                );
            } else {
                return (
                    <LoginLayout {...this.props}>
                    </LoginLayout>
                );
            }
        }
    }
};

/**
 * With Frontend layout
 */
const withFrontendLayout = (WrappedComponent, frontendTheme, pageId) => {

    return class WithAuthorization extends React.Component {
        componentDidMount() {
            document.body.classList.remove('admin-page');
            document.body.classList.add('front-page');
            if (frontendTheme) {
                document.body.classList.add(frontendTheme);
            }
        }

        componentWillMount() {
            const {location: {pathname}} = this.props;

            if (initialLoad && initialRoute) {
                if (initialRoute !== pathname && pathname === '/') {
                    window.location.replace(window.location.href + initialRoute);
                    initialLoad = false;
                }
            }
        }

        render() {
            let props = this.props;
            if (pageId) {
                props.params.page = pageId;
            }

            if ((isAuthorizationOn && isLogged()) || !isAuthorizationOn) {
                if (WrappedComponent) {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}>
                            <WrappedComponent {...props} frontendTheme={frontendTheme}/>
                        </FrontLayout>
                    );
                } else {
                    return (
                        <FrontLayout {...props} frontendTheme={frontendTheme}>
                        </FrontLayout>
                    );
                }

            } else {
                return (
                    <FrontLayout {...props} frontendTheme={frontendTheme}>
                        <LoginLayout {...this.props}>
                        </LoginLayout>
                    </FrontLayout>
                )
            }
        }
    }
};

/**
 * Render correct routes depending on renderer
 */
if (renderer === 'backend') {
    /**
     * Backend routes
     */
    ReactDOM.render(
        <Provider store={store}>
            <PersistGate persistor={gate}>
                <Router
                    history={history}
                    onUpdate={() => {
                        if (isAdmin()) {
                            store.dispatch(getGlobals());
                        }
                    }}
                >
                    <Route
                        path="/"
                        component={withBackendLayout(HomeBackend)}
                    />
                    <Route
                        path="/workspace/:workspace"
                        component={withBackendLayout(WorkspaceBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/page/:page/:language"
                        component={withBackendLayout(PageBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/page/add"
                        component={withBackendLayout(AddPageBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/block/:block/:language"
                        component={withBackendLayout(BlockBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/block/add"
                        component={withBackendLayout(AddBlockBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/menu/add"
                        component={withBackendLayout(AddMenuBackend)}
                    />
                    <Route
                        path="/workspace/:workspace/menu/:menu/:language"
                        component={withBackendLayout(MenuBackend)}
                    />
                    <Route
                        path="/add-global-field"
                        component={withBackendLayout(AddGlobalFieldBackend)}
                    />
                    <Route
                        path="/global-field/:key/:language"
                        component={withBackendLayout(GlobalFieldBackend)}
                    />
                    <Route
                        path="/add-workspace"
                        component={withBackendLayout(AddWorkspaceBackend)}
                    />
                    <Route
                        path="/media"
                        component={withBackendLayout(MediaBackend)}
                    />
                    <Route
                        path="/users"
                        component={withBackendLayout(UsersBackend)}
                    />
                    <Route
                        path="/preview"
                        component={withFrontendLayout(Preview, client)}
                    />
                    <Route
                        path="/logout"
                        component={withBackendLayout(LogoutBackend, client)}
                    />
                    <Redirect from="*" to="/"/>
                </Router>
            </PersistGate>
        </Provider>,
        document.getElementById('application')
    );
} else {
    /**
     * Frontend routes
     */
    switch (client) {

        case "awave":
            ReactDOM.render(
                <Provider store={store}>
                    <PersistGate persistor={gate}>
                        <Router history={history}>
                            <Route
                                path="/"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/preview"
                                component={withFrontendLayout(Preview, client)}
                            />
                            <Route
                                path="/page/:page"
                                component={withFrontendLayout(Page, client)}
                            />
                            <Redirect from="*" to="/"/>
                        </Router>
                    </PersistGate>
                </Provider>,
                document.getElementById('application')
            );
            break;
        case "electrolux-plp":
            ReactDOM.render(
                <Provider store={store}>
                    <PersistGate persistor={gate}>
                        <Router history={history}>
                            <Route path="/" component={withFrontendLayout(Home, client)}/>
                            <Route
                                path="/plp"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/archive"
                                component={withFrontendLayout(OngoingActionsArchive, client)}
                            />
                            <Route
                                path="/plp/elements"
                                component={withFrontendLayout(ElementGuidlines, client)}
                            />
                            <Route
                                path="/plp/employee-not-found"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/sso-success"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/dev-tests"
                                component={withFrontendLayout(DevTests, client)}
                            />
                            <Route
                                path="/plp/own-view"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/own-view/actions"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/own-view/ongoing-actions"
                                component={withFrontendLayout(OngoingActions, client)}
                            />

                            <Route
                                path="/plp/actions"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/ongoing-actions"
                                component={withFrontendLayout(OngoingActions, client)}
                            />


                            <Route
                                path="/plp/ongoing-actions/exit-checklist"
                                component={withFrontendLayout(OngoingActions, client)}
                            />
                            <Route
                                path="/plp/manager-view"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/plp/admin-view"
                                component={withFrontendLayout(Home, client)}
                            />

                            <Route
                                path="/page/:page"
                                component={withFrontendLayout(Page, client)}
                            />
                            <Redirect from="*" to="/"/>
                        </Router>
                    </PersistGate>
                </Provider>,
                document.getElementById("application")
            );
            break;

        case "electrolux-public":

            ReactDOM.render(
                <Provider store={store}>
                    <PersistGate persistor={gate}>
                        <Router history={history}>
                            <Route
                                path="/"
                                component={withFrontendLayout(Home, client)}
                            />
                            <Route
                                path="/preview"
                                component={withFrontendLayout(Preview, client)}
                            />
                            <Route
                                path="/page/:page/:service"
                                component={withFrontendLayout(Page, client)}
                            />
                            <Route
                                path="/page/:page"
                                component={withFrontendLayout(Page, client)}
                            />
                            <Redirect from="*" to="/"/>
                        </Router>
                    </PersistGate>
                </Provider>,
                document.getElementById('application')
            );
            break;
        case "electrolux":
        case "job-architecture":
        default:
            client = "job-architecture";
            document.title = "Electrolux Job Architecture";
            ReactDOM.render(
                <Provider store={store}>
                    <PersistGate persistor={gate}>
                        <Router history={history}>
                            <Route
                                path="/"
                                component={withFrontendLayout(Home, "job-architecture")}
                            />
                            <Route
                                path="/preview"
                                component={withFrontendLayout(Preview, "job-architecture")}
                            />
                            <Route
                                path="/page/:page"
                                component={withFrontendLayout(Page, "job-architecture")}
                            />
                            <Redirect from="*" to="/"/>
                        </Router>
                    </PersistGate>
                </Provider>,
                document.getElementById('application')
            );

            break;
    }

}

export {
    basename,
    store
};




// WEBPACK FOOTER //
// ./src/index.js